'use client';
/**
 * LA FICHE DE VIE — la frise de l'appareil (Ines, 29/10/2026).
 *
 * En tête : les indicateurs, puis l'ACQUISITION (le dossier de mise en service),
 * rendue à part parce qu'elle n'est pas un événement parmi d'autres — c'est la
 * naissance de l'appareil. Dessous : un tableau unique (03/11/2026, était une
 * frise), du plus récent au plus ancien, de toutes ses NC, maintenances,
 * opérations de métrologie et changements d'état. L'assemblage est un module pur : `@/lib/vie-equipement`.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { Pastille } from '@/components/ui';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { fmtDate, aujourdhuiIso } from '@/lib/meta';
import { ncsDeLEquipement } from '@/lib/nc-equipement';
import type { GedEquipement, EtatMaintenance } from '@/lib/types';
import type { EntreeHistorique } from '@/lib/historique-maintenance';
import { assemblerVie, FAMILLES, ageLisible, type Calibration, type Evenement, type Famille } from '@/lib/vie-equipement';

const TON: Record<Famille, 'danger' | 'info' | 'success' | 'neutral'> = {
  nc: 'danger', maintenance: 'info', metrologie: 'success', etat: 'neutral',
};

function Indicateur({ valeur, quoi, alerte }: { valeur: React.ReactNode; quoi: string; alerte?: boolean }) {
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', padding: '9px 11px', minWidth: 0 }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: alerte ? 'var(--red-600)' : 'var(--text-strong)' }}>{valeur}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.4 }}>{quoi}</div>
    </div>
  );
}

export function VieEquipement({ e, onVersMaintenance, onVersCalibrations }: {
  e: GedEquipement;
  /** Spec 002, récit 1 : une maintenance cliquée ouvre SON détail dans l'onglet Maintenance. */
  onVersMaintenance?: (cible?: { intervention?: string }) => void;
  onVersCalibrations?: () => void;
}) {
  const { ncs, liens, events, loadJournal, go, select } = useGed();
  const code = e.code || '';
  const [maintenances, setMaintenances] = React.useState<EntreeHistorique[] | null>(null);
  const [calibrations, setCalibrations] = React.useState<Calibration[] | null>(null);
  /* L'ÉTAT DE MAINTENANCE de l'appareil (spec 002, récit 7) : la prochaine
     échéance et les retards se LISENT, le même calcul que l'en-tête du dossier.
     Une lecture qui échoue laisse `null` — l'indicateur affiche « — ». */
  const [etatMnt, setEtatMnt] = React.useState<EtatMaintenance | null>(null);

  React.useEffect(() => {
    if (!code) return;
    let vivant = true;
    void loadJournal(code);
    void fetch(`/api/ged/ged/maintenances/historique?equipement=${encodeURIComponent(code)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setMaintenances((j?.entrees || []) as EntreeHistorique[]); })
      .catch(() => { if (vivant) setMaintenances([]); });
    void fetch(`/api/ged/ged/calibrations/journal?equipement=${encodeURIComponent(code)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setCalibrations((j?.lignes || []) as Calibration[]); })
      .catch(() => { if (vivant) setCalibrations([]); });
    void fetch(`/api/ged/ged/maintenances/equipement/${encodeURIComponent(code)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setEtatMnt((j?.etat || null) as EtatMaintenance | null); })
      .catch(() => { /* l'indicateur reste vide, sans date inventée */ });
    return () => { vivant = false; };
  }, [code, loadJournal]);

  const ncsEquip = React.useMemo(() => ncsDeLEquipement(ncs, liens, code), [ncs, liens, code]);
  const journal = React.useMemo(() => events[code] || [], [events, code]);
  const vie = React.useMemo(() => assemblerVie({
    equipement: e, ncs: ncsEquip, maintenances: maintenances || [], calibrations: calibrations || [],
    journal, etatMaintenance: etatMnt, aujourdhui: aujourdhuiIso(),
  }), [e, ncsEquip, maintenances, calibrations, journal, etatMnt]);

  if (maintenances === null || calibrations === null) {
    return <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture de la vie de l’appareil…</p>;
  }

  const { acquisition: acq, indicateurs: ind } = vie;
  const auj = aujourdhuiIso();
  const ouvrable = (x: Evenement) => x.lien?.type === 'nc'
    || (x.lien?.type === 'maintenance' && !!onVersMaintenance)
    || (x.lien?.type === 'calibrations' && !!onVersCalibrations);
  const ouvrir = (x: Evenement) => {
    if (x.lien?.type === 'nc') { select('selectedNC', x.lien.documentId); go('nc'); }
    else if (x.lien?.type === 'maintenance') onVersMaintenance?.(x.lien.id ? { intervention: x.lien.id } : undefined);
    else if (x.lien?.type === 'calibrations') onVersCalibrations?.();
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {/* ── Indicateurs ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
        <Indicateur valeur={ind.ageMois === null ? '—' : ageLisible(ind.ageMois)} quoi="en service" />
        <Indicateur valeur={`${ind.pannes12m} / ${ind.pannesTotal}`} quoi="pannes sur 12 mois / depuis l’origine" alerte={ind.pannes12m >= 3} />
        <Indicateur valeur={ind.ncOuvertes} quoi="NC ouverte(s)" alerte={ind.ncOuvertes > 0} />
        <Indicateur valeur={`${ind.joursArret.toLocaleString('fr-FR')} j`} quoi={`d’arrêt cumulé${ind.arretsIllisibles ? ` (+ ${ind.arretsIllisibles} durée(s) illisible(s))` : ''}`} />
        <Indicateur valeur={ind.derniereMaintenance ? fmtDate(ind.derniereMaintenance) : '—'} quoi="dernière maintenance" />
        <Indicateur valeur={ind.prochaineEcheance ? fmtDate(ind.prochaineEcheance) : '—'}
          quoi={`prochaine échéance${ind.prochaineNature === 'metrologie' ? ' (métrologie)' : ind.prochaineNature === 'maintenance' ? ' (maintenance)' : ''}${ind.echeancesEnRetard ? ` — ${ind.echeancesEnRetard} en retard` : ''}`}
          alerte={ind.echeancesEnRetard > 0} />
        <Indicateur valeur={ind.finGarantie ? (fmtDate(ind.finGarantie) || ind.finGarantie) : '—'} quoi="fin de garantie" />
        <Indicateur valeur={ind.finContrat ? fmtDate(ind.finContrat) : '—'} quoi="fin du contrat de maintenance" alerte={!!ind.finContrat && ind.finContrat < auj} />
      </div>

      {/* ── Acquisition ─────────────────────────────────────────────────── */}
      {/* À part, par le fond et non par un filet coloré : c'est la naissance de
          l'appareil, pas un événement de plus. */}
      <section style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px', display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 13.5, color: 'var(--text-strong)' }}>Acquisition et mise en service</h3>
          {acq.dossier?.autoriseLe
            ? <Pastille ton="success" compacte>usage autorisé le {fmtDate(acq.dossier.autoriseLe)}</Pastille>
            : acq.dossier ? <Pastille ton="warning" compacte>mise en service en cours</Pastille> : null}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6 }}>
          {[acq.dateReception && `Réception : ${fmtDate(acq.dateReception) || acq.dateReception}`,
            acq.dateMiseEnService && `Mise en service : ${fmtDate(acq.dateMiseEnService) || acq.dateMiseEnService}`,
            acq.fournisseur && `Fournisseur : ${acq.fournisseur}`,
            acq.garantieJusquA && `Garantie jusqu’au ${fmtDate(acq.garantieJusquA) || acq.garantieJusquA}`].filter(Boolean).join(' · ')
            || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Ni réception ni mise en service renseignées.</span>}
        </div>
        {acq.dossier ? (
          <>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              Dossier ouvert le {fmtDate(acq.dossier.ouvertLe)} — {acq.dossier.faites} étape(s) faite(s) sur {acq.dossier.retenues}.
            </div>
            <ol style={{ margin: 0, paddingInlineStart: 20, fontSize: 12.5, lineHeight: 1.6 }}>
              {acq.dossier.etapes.map((x, i) => (
                <li key={i} style={{ color: x.sansObjet ? 'var(--text-faint)' : 'var(--text-body)' }}>
                  {x.libelle}
                  {x.sansObjet ? ' — sans objet' : x.fait ? <span style={{ color: 'var(--green-700)' }}> — fait{x.faitLe ? ` le ${fmtDate(x.faitLe)}` : ''}</span>
                    : <span style={{ color: x.bloquante ? 'var(--amber-700)' : 'var(--text-muted)' }}> — à faire{x.bloquante ? ' (bloquante)' : ''}</span>}
                </li>
              ))}
            </ol>
          </>
        ) : (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Pas de dossier de mise en service : l’appareil a été repris du classeur. Ce qui précède vient de sa fiche signalétique.
          </div>
        )}
      </section>

      {/* ── Les événements, en TABLEAU (Ines, 03/11/2026 : « afficher la fiche
          de vie en tableau plus lisible »). La frise en liste ne se triait ni ne
          se filtrait par colonne ; le tableau commun le fait (date du/au, type,
          nature en liste, recherche). Une ligne qui mène quelque part (NC,
          maintenance, calibration) s'ouvre d'un clic. */}
      <Tableau
        id={`vie-${code}`}
        niveau="standard"
        recherche="Rechercher : panne, prestataire, paramètre…"
        ajusterLargeur
        lignes={vie.evenements}
        cleLigne={(x) => x.cle}
        vide={{ titre: 'Aucun événement enregistré depuis l’acquisition.' }}
        ligneProps={(x) => (ouvrable(x) ? ligneOuvrable(() => ouvrir(x), `Ouvrir : ${x.titre}`) : {})}
        colonnes={[
          {
            cle: 'date', libelle: 'Date', type: 'date', valeur: (x) => x.date || '',
            rendu: (x) => <span style={{ fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{x.date ? fmtDate(x.date) : 'sans date'}</span>,
          },
          {
            cle: 'type', libelle: 'Type', type: 'liste', valeur: (x) => FAMILLES.find((f) => f.k === x.famille)?.label || x.famille,
            rendu: (x) => <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{FAMILLES.find((f) => f.k === x.famille)?.label || x.famille}</span>,
          },
          {
            cle: 'nature', libelle: 'Nature', type: 'liste', valeur: (x) => x.nature,
            rendu: (x) => <Pastille ton={x.alerte ? 'danger' : TON[x.famille]} compacte>{x.nature}</Pastille>,
          },
          {
            cle: 'evenement', libelle: 'Événement', valeur: (x) => x.titre, style: { whiteSpace: 'normal' },
            rendu: (x) => <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{x.titre}</span>,
          },
          {
            cle: 'detail', libelle: 'Détail', valeur: (x) => x.detail, style: { whiteSpace: 'normal', color: 'var(--text-muted)' },
            rendu: (x) => x.detail || '',
          },
        ]}
      />
    </div>
  );
}
